import { addClass, removeClass } from "dom-helpers";
import isNumber from "lodash/isNumber";
import { EventChannel } from "redux-saga";
import { call, delay, fork, put, race, select, take, takeEvery, takeLatest } from "redux-saga/effects";
import type { ReadyEvent, Reveal, SlideChangedEvent } from "reveal.js";

import type { RegisteredScreenKey } from "../../screen-registry";
import { ColorMode, LogoPosition } from "../../types";
import { createRevealDeck } from "./create-reveal-deck";
import deckSlice, { changeColorMode, changeLogoPosition, initDeck } from "./deck.slice";
import { createRevealEventChannel } from "./reveal-event-channel";
import { getIsVerticalNavigation } from "@/lib/selectors";

function getSlideStateFromEvent(event: ReadyEvent | SlideChangedEvent) {
  return {
    prevSlideId: event["previousSlide"]?.dataset.slideId,
    currentSlideId: event.currentSlide?.dataset.slideId as RegisteredScreenKey,
    currentSlideIndex: event.indexh,
    colorMode: event.currentSlide?.dataset.colorMode as ColorMode,
    logoPosition: event.currentSlide?.dataset.logoPosition as LogoPosition,
  };
}

function* watchRevealEvents(reveal: Reveal) {
  const readyEventChannel: EventChannel<ReadyEvent> = yield call(createRevealEventChannel, reveal, "ready");
  const slideChangedEventChannel: EventChannel<SlideChangedEvent> = yield call(
    createRevealEventChannel,
    reveal,
    "slidechanged"
  );
  const slidetransitionendEventChannel: EventChannel<SlideChangedEvent> = yield call(
    createRevealEventChannel,
    reveal,
    "slidetransitionend"
  );

  yield takeEvery(readyEventChannel, function* (event: ReadyEvent) {
    yield put(deckSlice.actions.ready(getSlideStateFromEvent(event)));

    const { colorMode, logoPosition } = yield select(state => state.deck);

    yield put(changeColorMode(colorMode));
    yield put(changeLogoPosition(logoPosition));
  });

  yield takeLatest(slideChangedEventChannel, function* (event: SlideChangedEvent) {
    const slideState = getSlideStateFromEvent(event);
    if (!slideState.prevSlideId) return;

    const { colorMode, logoPosition } = yield select(state => state.deck);

    if (colorMode !== slideState.colorMode) {
      yield put(changeColorMode(slideState.colorMode));
    }

    if (logoPosition !== slideState.logoPosition) {
      yield put(changeLogoPosition(slideState.logoPosition));
    }

    const isVerticalNavigation = yield select(getIsVerticalNavigation);

    if (isVerticalNavigation) {
      /**
       * Reveal doesn't update the hash automatically when using vertical/scroll navigation, so update it manually
       * If @see https://github.com/hakimel/reveal.js/issues/3815 ends up getting resolved,
       * we should see if that fixes this issue as well and potentially remove this logic
       */
      yield call(reveal.sync);
    } else {
      yield race({
        // Wait for the slidetransitionend event before dispatching the slidechanged action
        transitionEnd: take(slidetransitionendEventChannel),
        // If we are jumping too far for a transition event, just cancel after 1800ms and dispatch slidechanged event
        timeout: delay(1800),
      });
    }

    yield put(deckSlice.actions.slidechanged(slideState));
  });
}

function* createAndWatchRevealDeck() {
  const options = yield select(state => state.deck.options);
  const reveal: Reveal = yield call(createRevealDeck, options);

  yield fork(watchRevealEvents, reveal);

  yield call(function () {
    reveal.initialize();
  });

  yield takeEvery(deckSlice.actions.gotoPrev.type, function* () {
    yield call(reveal.left);
  });

  yield takeEvery(deckSlice.actions.gotoNext.type, function* () {
    yield call(reveal.right);
  });

  yield takeLatest(deckSlice.actions.gotoIndex, function* ({ payload: indexh }) {
    if (!isNumber(indexh)) return;

    const isVerticalNavigation = yield select(getIsVerticalNavigation);

    /**
     * NOTE: This is a hack to manually scroll to the active slide on our side.
     * Reveal.js has a bug with vertical scroll mode where the hashes are incorrect past the halfway mode of the presentation
     * If/when they fix the issue on their side, we should remove this if block and call the `reveal.slide` API method like normal
     * @see https://github.com/hakimel/reveal.js/issues/3815
     */
    if (isVerticalNavigation) {
      const slide = document.querySelector(`section[data-index-h="${indexh}"]`) as HTMLElement;

      if (slide) {
        slide.scrollIntoView({ behavior: "instant", block: "start" });
      }

      return;
    }

    yield call(reveal.slide, indexh, 0);
  });

  yield takeLatest(deckSlice.actions.syncSlideById, function* ({ payload: slideId }) {
    yield call(function* () {
      const slide = document.querySelector(`section[data-slide-id="${slideId}"]`) as HTMLElement;

      const { colorMode, logoPosition } = yield select(state => state.deck);

      if (colorMode !== slide?.dataset.colorMode) {
        yield put(changeColorMode(slide?.dataset.colorMode as ColorMode));
      }

      if (logoPosition !== slide?.dataset.logoPosition) {
        yield put(changeLogoPosition(slide?.dataset.logoPosition as LogoPosition));
      }

      if (slide) {
        try {
          reveal.syncSlide(slide);
        } catch (e) {
          console.warn(`Error syncing slide "${slideId}"`, e);
        }
      }
    });
  });

  yield takeLatest(
    deckSlice.actions.changeSlideBackground,
    function* ({ payload: { id: slideId, color, videoUrl, imageUrl, backgroundType } }) {
      yield call(function () {
        const slide = document.querySelector(`section[data-slide-id="${slideId}"]`) as HTMLDivElement;
        if (!slide) return;

        if (backgroundType === "video" && videoUrl) {
          slide.dataset.backgroundVideo = videoUrl;
          try {
            reveal.syncSlide(slide);
          } catch (e) {
            console.warn(`Error syncing slide "${slideId}"`, e);
          }
        }

        if (backgroundType === "image" && imageUrl) {
          slide.dataset.backgroundImage = imageUrl;
          try {
            reveal.syncSlide(slide);
          } catch (e) {
            console.warn(`Error syncing slide "${slideId}"`, e);
          }
        }

        if (color && slide.dataset.backgroundColor !== color) {
          slide.dataset.backgroundColor = color;
          try {
            reveal.syncSlide(slide);
          } catch (e) {
            console.warn(`Error syncing slide "${slideId}"`, e);
          }
        }
      });
    }
  );

  yield takeLatest(deckSlice.actions.changeColorMode, function* ({ payload: colorMode }) {
    yield call(function () {
      const rootEl = reveal.getRevealElement() as HTMLDivElement;
      if (!rootEl) return;

      const darkClassName = "dark";

      if (colorMode === "dark") {
        addClass(rootEl, darkClassName);
      } else {
        removeClass(rootEl, darkClassName);
      }
    });
  });

  yield takeLatest(deckSlice.actions.sync.type, function* () {
    yield call(() => reveal.sync());
    yield call(() => reveal.layout());
  });
}

export function* deckSaga() {
  // Wait until deck is initialized
  yield takeLatest(initDeck.type, createAndWatchRevealDeck);
}
